import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCollectionDetail } from '../../../api';

const CollectionDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchCollectionDetail = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getCollectionDetail(id);
                setCollection(data);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch collection details.');
                console.error('Error fetching collection details:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchCollectionDetail();
        }
    }, [id, navigate]);

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ color: '#707F81', fontSize: '1.2rem' }}>Loading collection details...</div>
            </div>
        );
    }

    if (error || !collection) {
        return (
            <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '100px' }}>
                <div style={{ color: '#dc2626', background: '#fee2e2', padding: '16px 24px', borderRadius: '8px', marginBottom: '20px' }}>
                    {error || 'Collection not found'}
                </div>
                <button onClick={() => navigate('/collections-list')} style={{
                    padding: '10px 20px',
                    background: '#3c5a5d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                }}>Go Back</button>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px' }}>
            <div style={{
                maxWidth: '1000px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <button
                        onClick={() => navigate('/collections-list')}
                        style={{
                            background: 'transparent',
                            border: '1px solid #cfd8dc',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#3c5a5d',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e5eff8' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    >
                        ← Back to Collections
                    </button>
                </div>

                {/* Collection Info section */}
                <div style={{
                    paddingBottom: '30px',
                    borderBottom: '1px solid #e2e8f0',
                    marginBottom: '30px'
                }}>
                    <h1 style={{ color: '#3c5a5d', margin: '0 0 16px 0', fontSize: '2.5rem' }}>
                        {collection.collection_name || 'Untitled Collection'}
                    </h1>
                    
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        {collection.cards && (
                            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '6px 14px', borderRadius: '20px', fontSize: '0.95rem', fontWeight: '600' }}>
                                {collection.cards.length} Cards
                            </span>
                        )}
                        {collection.level && (
                            <span style={{ background: '#f3e8ff', color: '#6b21a8', padding: '6px 14px', borderRadius: '20px', fontSize: '0.95rem', fontWeight: '600', textTransform: 'capitalize' }}>
                                {collection.level}
                            </span>
                        )}
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {collection.language && (
                                <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '6px 14px', borderRadius: '20px 0 0 20px', fontSize: '0.95rem', fontWeight: '600', textTransform: 'uppercase' }}>
                                    {collection.language}
                                </span>
                            )}
                            {collection.translation_language && (
                                <span style={{ background: '#f1f5f9', color: '#475569', padding: '6px 14px', borderRadius: '0 20px 20px 0', fontSize: '0.95rem', fontWeight: '600', textTransform: 'uppercase', borderLeft: '1px solid #cbd5e1' }}>
                                    {collection.translation_language}
                                </span>
                            )}
                        </div>
                        {collection.published !== undefined && (
                            <span style={{ 
                                background: collection.published ? '#dcfce7' : '#fef2f2', 
                                color: collection.published ? '#166534' : '#991b1b', 
                                padding: '6px 14px', 
                                borderRadius: '20px', 
                                fontSize: '0.95rem', 
                                fontWeight: '600' 
                            }}>
                                {collection.published ? 'Published' : 'Private'}
                            </span>
                        )}
                        {collection.is_learned && (
                            <span style={{ background: '#fef08a', color: '#854d0e', padding: '6px 14px', borderRadius: '20px', fontSize: '0.95rem', fontWeight: '700' }}>
                                ✓ Learned
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                        {collection.collection_description && (
                            <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', flex: '1', minWidth: '300px' }}>
                                <h4 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</h4>
                                <p style={{ margin: 0, color: '#334155', lineHeight: '1.6', fontSize: '1.1rem' }}>{collection.collection_description}</p>
                            </div>
                        )}
                        
                        {collection.owner && (
                            <div style={{ padding: '20px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', minWidth: '240px' }}>
                                <h4 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created By</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#3c5a5d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                        {collection.owner.name?.charAt(0) || 'U'}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#334155' }}>{collection.owner.name}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{collection.owner.email}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Additional metadata or card list could go here */}
                {collection.cards && collection.cards.length > 0 && (
                    <div>
                        <h2 style={{ color: '#3c5a5d', marginBottom: '20px', fontSize: '1.8rem' }}>Cards</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                            {collection.cards.map((card, idx) => (
                                <div key={card.id || idx} style={{
                                    background: '#fff',
                                    padding: '24px',
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '1.2rem', marginBottom: '4px' }}>{card.word}</div>
                                        <div style={{ color: '#64748b', fontSize: '1rem', fontStyle: 'italic' }}>{card.translation}</div>
                                    </div>
                                    
                                    {(card.usage_example || card.usage_example_translation) && (
                                        <div style={{ 
                                            padding: '12px', 
                                            background: '#f1f5f9', 
                                            borderRadius: '8px',
                                            marginTop: '8px',
                                            fontSize: '0.9rem'
                                        }}>
                                            {card.usage_example && <div style={{ color: '#334155', fontWeight: '500', marginBottom: '4px' }}>{card.usage_example}</div>}
                                            {card.usage_example_translation && <div style={{ color: '#64748b' }}>{card.usage_example_translation}</div>}
                                        </div>
                                    )}

                                    {card.learning_progress !== undefined && (
                                        <div style={{ marginTop: 'auto', pt: '10px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>
                                                <span>Progress</span>
                                                <span>{card.learning_progress}%</span>
                                            </div>
                                            <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', background: '#10b981', width: `${card.learning_progress}%` }}></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CollectionDetails;
